import React, { useState, useEffect } from 'react';
import { HttpService } from '../../game/services/HttpService';
import './JoinGame.component.css';

interface PokerTable {
  id: string;
  roomId: string;
  host: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  isRunning: boolean;
  tableSeats: number;
  gameType: string;
  chips: number;
  bigBlind: number;
  smallBlind: number;
  minEntryChips: number;
  maxEntryChips: number;
  creationTime: string;
}

interface JoinGameProps {
  onClose: () => void;
  onJoinGame: (roomId: string) => void;
  /** When provided, running games show "Open table" (observer) and "Open lobby" */
  onOpenTable?: (roomId: string) => void;
  /** When opening lobby for a running game, pass isRunning so user is not auto-seated */
  onOpenLobby?: (roomId: string, isRunning?: boolean) => void;
}

const JoinGame: React.FC<JoinGameProps> = ({ onClose, onJoinGame, onOpenTable, onOpenLobby }) => {
  const [joinRoomId, setJoinRoomId] = useState('');
  const [joinError, setJoinError] = useState('');
  const [runningGames, setRunningGames] = useState<PokerTable[]>([]);
  const [availableGames, setAvailableGames] = useState<PokerTable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'available' | 'running'>('available');

  const httpService = new HttpService();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (token) {
      httpService.setToken(token);
      loadGames();
      const interval = setInterval(loadGames, 15000);
      return () => clearInterval(interval);
    }
  }, []);

  const loadGames = async () => {
    setLoading(true);
    setError('');
    try {
      const [runningResponse, availableResponse] = await Promise.all([
        httpService.get<any>('poker-tables/running'),
        httpService.get<any>('poker-tables/available'),
      ]);
      if (runningResponse.status === 'success' && runningResponse.data) {
        setRunningGames(Array.isArray(runningResponse.data) ? runningResponse.data : []);
      }
      if (availableResponse.status === 'success' && availableResponse.data) {
        setAvailableGames(Array.isArray(availableResponse.data) ? availableResponse.data : []);
      }
    } catch (err: any) {
      console.error('Error loading games:', err);
      setError(err.message || 'Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinById = () => {
    setJoinError('');
    if (!joinRoomId.trim()) {
      setJoinError('Please enter a room ID');
      return;
    }
    if (joinRoomId.trim().length < 5) {
      setJoinError('Room ID must be at least 5 characters');
      return;
    }
    onJoinGame(joinRoomId.trim());
    onClose();
  };

  const getGameTypeLabel = (gameType: string) => {
    const labels: Record<string, string> = {
      'TXS_NL': 'Texas Hold\'em No Limit',
      'TXS_LM': 'Texas Hold\'em Limit',
      'PLO_4': 'Pot Limit Omaha 4',
      'PLO_5': 'Pot Limit Omaha 5',
    };
    return labels[gameType] || gameType;
  };

  const getHostName = (host: any) => {
    return `${host.firstName || ''} ${host.lastName || ''}`.trim() || host.email;
  };

  const formatTime = (timeString: string) => {
    try {
      return new Date(timeString).toLocaleString();
    } catch {
      return timeString;
    }
  };

  return (
    <div className="join-game-overlay" onClick={onClose}>
      <div className="join-game-modal" onClick={(e) => e.stopPropagation()}>
        <div className="join-game-header">
          <h2>Join Game</h2>
          <button type="button" className="join-game-close" onClick={onClose}>×</button>
        </div>

        <div className="join-game-body">
          <section className="join-by-id-section">
            <h3>Join game by ID</h3>
            <div className="join-by-id-row">
              <input
                type="text"
                value={joinRoomId}
                onChange={(e) => setJoinRoomId(e.target.value)}
                placeholder="Enter room ID"
                onKeyDown={(e) => e.key === 'Enter' && handleJoinById()}
              />
              <button type="button" className="btn-join-by-id" onClick={handleJoinById}>
                Join Game
              </button>
            </div>
            {joinError && <div className="join-game-error">{joinError}</div>}
          </section>

          {error && <div className="join-game-list-error">{error}</div>}

          <div className="join-game-tabs">
            <button
              type="button"
              className={activeTab === 'available' ? 'active' : ''}
              onClick={() => setActiveTab('available')}
            >
              Available games ({availableGames.length})
            </button>
            <button
              type="button"
              className={activeTab === 'running' ? 'active' : ''}
              onClick={() => setActiveTab('running')}
            >
              Running games ({runningGames.length})
            </button>
          </div>

          <div className="join-game-content">
            {loading && availableGames.length === 0 && runningGames.length === 0 ? (
              <div className="join-game-loading">Loading games...</div>
            ) : activeTab === 'available' ? (
              <div className="join-game-list">
                {availableGames.length === 0 ? (
                  <p className="join-game-empty">No available games</p>
                ) : (
                  availableGames.map((game) => (
                    <div key={game.id} className="join-game-item">
                      <div className="join-game-info">
                        <div className="join-game-item-header">
                          <span className="join-game-status available">○ Available</span>
                          <span className="join-game-type">{getGameTypeLabel(game.gameType)}</span>
                        </div>
                        <div className="join-game-details">
                          <div className="join-game-detail-row">
                            <span className="detail-label">Host:</span>
                            <span className="detail-value">{getHostName(game.host)}</span>
                          </div>
                          <div className="join-game-detail-row">
                            <span className="detail-label">Room ID:</span>
                            <span className="detail-value room-id">{game.roomId}</span>
                          </div>
                          <div className="join-game-detail-row">
                            <span className="detail-label">Blinds:</span>
                            <span className="detail-value">${game.smallBlind}/${game.bigBlind}</span>
                          </div>
                          <div className="join-game-detail-row">
                            <span className="detail-label">Buy-in:</span>
                            <span className="detail-value">${game.minEntryChips} - ${game.maxEntryChips}</span>
                          </div>
                          <div className="join-game-detail-row">
                            <span className="detail-label">Seats:</span>
                            <span className="detail-value">{game.tableSeats}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="join-game-btn"
                        onClick={() => {
                          onJoinGame(game.roomId);
                          onClose();
                        }}
                      >
                        Join Game
                      </button>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="join-game-list">
                {runningGames.length === 0 ? (
                  <p className="join-game-empty">No games currently running</p>
                ) : (
                  runningGames.map((game) => (
                    <div key={game.id} className="join-game-item">
                      <div className="join-game-info">
                        <div className="join-game-item-header">
                          <span className="join-game-status running">● Running</span>
                          <span className="join-game-type">{getGameTypeLabel(game.gameType)}</span>
                        </div>
                        <div className="join-game-details">
                          <div className="join-game-detail-row">
                            <span className="detail-label">Host:</span>
                            <span className="detail-value">{getHostName(game.host)}</span>
                          </div>
                          <div className="join-game-detail-row">
                            <span className="detail-label">Room ID:</span>
                            <span className="detail-value room-id">{game.roomId}</span>
                          </div>
                          <div className="join-game-detail-row">
                            <span className="detail-label">Blinds:</span>
                            <span className="detail-value">${game.smallBlind}/${game.bigBlind}</span>
                          </div>
                          <div className="join-game-detail-row">
                            <span className="detail-label">Buy-in:</span>
                            <span className="detail-value">${game.minEntryChips} - ${game.maxEntryChips}</span>
                          </div>
                          <div className="join-game-detail-row">
                            <span className="detail-label">Seats:</span>
                            <span className="detail-value">{game.tableSeats}</span>
                          </div>
                          <div className="join-game-detail-row">
                            <span className="detail-label">Started:</span>
                            <span className="detail-value">{formatTime(game.creationTime)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="join-game-actions">
                        {onOpenTable && (
                          <button
                            type="button"
                            className="join-game-btn join-game-btn-open-table"
                            onClick={() => {
                              onOpenTable(game.roomId);
                              onClose();
                            }}
                            title="Open game table as observer (watch only)"
                          >
                            Open table
                          </button>
                        )}
                        <button
                          type="button"
                          className="join-game-btn join-game-btn-open-lobby"
                          onClick={() => {
                            if (onOpenLobby) {
                              onOpenLobby(game.roomId, game.isRunning);
                            } else {
                              onJoinGame(game.roomId);
                            }
                            onClose();
                          }}
                          title={onOpenTable ? 'Open lobby in a new window' : 'Open lobby'}
                        >
                          Open lobby
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinGame;
